import styled from "@emotion/styled";
import { Box, Stack, Tab, Tabs, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Button } from "components/UI";
import { useChurchStore } from "features/churches";
import {
  useFinanceSettingsStore,
  useTeamBudgetsPaginated
} from "features/finances";
import { FILTER_OPERATIONS } from "features/members";
import { useTeam } from "features/teams";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const Container = styled.div`
  grid-column: 2 / 3;
  grid-row: 1 / 3;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
`;

export const BudgetTimelineTable = ({ teamId, budgetYear }) => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [activeTab, setActiveTab] = useState(0);

  const openSetBudgetModal = useFinanceSettingsStore(
    state => state.openSetBudgetModal
  );
  const selectedChurch = useChurchStore(state => state.selectedChurch);

  const teamQuery = useTeam({
    churchId: selectedChurch?.id,
    teamId,
    queries: {
      enabled: !!teamId
    }
  });

  const teamBudgetsQuery = useTeamBudgetsPaginated({
    churchId: selectedChurch?.id,
    teamId,
    page,
    pageSize,
    queries: {
      filter: {
        budget_year_id: [
          {
            operator: FILTER_OPERATIONS.EQUAL,
            value: budgetYear
          }
        ]
      }
    },
    config: {
      enabled: !!teamId && !!budgetYear
    }
  });

  const navigate = useNavigate();

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const columns = useMemo(() => {
    if (activeTab === 0) {
      return [
        {
          field: "item",
          headerName: "Item",
          flex: 1
        },
        {
          field: "item_gross_amount",
          headerName: "Amount",
          flex: 1,
          valueFormatter: ({ value }) => {
            return Number(value).toLocaleString();
          }
        },
        {
          field: "quarter_1_amount",
          headerName: "Q1",
          flex: 1,
          valueFormatter: ({ value }) => {
            return Number(value).toLocaleString();
          }
        },
        {
          field: "quarter_2_amount",
          headerName: "Q2",
          flex: 1,
          valueFormatter: ({ value }) => {
            return Number(value).toLocaleString();
          }
        },
        {
          field: "quarter_3_amount",
          headerName: "Q3",
          flex: 1,
          valueFormatter: ({ value }) => {
            return Number(value).toLocaleString();
          }
        },
        {
          field: "quarter_4_amount",
          headerName: "Q4",
          flex: 1,
          valueFormatter: ({ value }) => {
            return Number(value).toLocaleString();
          }
        }
      ];
    }

    if (activeTab === 1) {
      return [
        {
          field: "item",
          headerName: "Item",
          flex: 1
        },
        {
          field: "item_gross_amount",
          headerName: "Budget Amount",
          flex: 1,
          valueFormatter: ({ value }) => {
            return Number(value).toLocaleString();
          }
        },
        {
          field: "budget_used",
          headerName: "Actual Amount",
          flex: 1,
          valueFormatter: ({ value }) => {
            return Number(value).toLocaleString();
          }
        },
        {
          field: "budget_left",
          headerName: "Remaining Amount",
          flex: 1,
          valueFormatter: ({ value }) => {
            return Number(value).toLocaleString();
          }
        }
      ];
    }
  }, [activeTab]);

  const totalBudget = useMemo(
    () =>
      Number(
        teamBudgetsQuery.data?.result?.total_team_budget || 0
      ).toLocaleString() || 0,
    [teamBudgetsQuery.data]
  );

  const totalAmountUsed = useMemo(
    () =>
      Number(
        teamBudgetsQuery.data?.result?.total_team_budget_used || 0
      ).toLocaleString() || 0,
    [teamBudgetsQuery.data]
  );

  const totalAmountLeft = useMemo(
    () =>
      Number(
        teamBudgetsQuery.data?.result?.total_team_budget_left || 0
      ).toLocaleString() || 0,
    [teamBudgetsQuery.data]
  );

  return (
    <Stack spacing={3}>
      <Typography variant="h5" fontWeight="bold" color="primary.main">
        {teamQuery.data?.result?.name} Annual Budget
      </Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 4 }}>
        <Stack
          spacing={2}
          p={3}
          sx={{
            borderRadius: 2,
            backgroundColor: "secondary.main"
          }}
        >
          <Stack>
            <Typography variant="h6" fontWeight="bold" color="primary.main">
              Total Annual Budget
            </Typography>
            <Typography variant="caption">
              Total annual budget assigned to {teamQuery.data?.result?.name}
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h4" fontWeight="bold" color="primary.main">
              {totalBudget}
            </Typography>
            <Typography variant="caption">ETB</Typography>
          </Stack>
        </Stack>
        <Stack
          spacing={2}
          p={3}
          sx={{
            borderRadius: 2,
            backgroundColor: "secondary.main"
          }}
        >
          <Stack>
            <Typography variant="h6" fontWeight="bold" color="primary.main">
              Total Amount Used
            </Typography>
            <Typography variant="caption">
              Amount spent from the budget
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h4" fontWeight="bold" color="primary.main">
              {totalAmountUsed}
            </Typography>
            <Typography variant="caption">ETB</Typography>
          </Stack>
        </Stack>
        <Stack
          spacing={2}
          p={3}
          sx={{
            borderRadius: 2,
            backgroundColor: "secondary.main"
          }}
        >
          <Stack>
            <Typography variant="h6" fontWeight="bold" color="primary.main">
              Total Amount Left
            </Typography>
            <Typography variant="caption">
              Amount remaining from total budget
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h4" fontWeight="bold" color="primary.main">
              {totalAmountLeft}
            </Typography>
            <Typography variant="caption">ETB</Typography>
          </Stack>
        </Stack>
      </Box>

      <Container>
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            aria-label="finance report tabs"
          >
            <Tab label="Budget Timeline" />
            <Tab label="Budget vs Actual" />
          </Tabs>
        </Box>
        {activeTab === 0 && (
          <Header>
            <Button onClick={() => openSetBudgetModal()}>Add Item</Button>
          </Header>
        )}
        {activeTab === 0 && (
          <DataGrid
            autoHeight
            rows={teamBudgetsQuery.data?.result?.data || []}
            rowCount={teamBudgetsQuery.data?.result?.total || 0}
            loading={teamBudgetsQuery.isLoading}
            columns={columns}
            pagination
            rowsPerPageOptions={[5, 10, 15, 25, 50, 100]}
            density="comfortable"
            getRowId={row => row.id}
            disableColumnMenu
            disableSelectionOnClick
            paginationMode="server"
            page={page - 1}
            pageSize={pageSize}
            onPageChange={newPage => setPage(newPage + 1)}
            onPageSizeChange={newPageSize => setPageSize(newPageSize)}
            localeText={{
              noRowsLabel: t("teams.teamProfile.NoRows"),
              MuiTablePagination: {
                labelRowsPerPage: t("teams.teamProfile.RowsPerPage")
              }
            }}
            onRowClick={({ row }) => {
              navigate(`/finances/temp/budget/${row.team.id}`);
            }}
          />
        )}

        {activeTab === 1 && (
          <DataGrid
            autoHeight
            rows={teamBudgetsQuery.data?.result?.data || []}
            rowCount={teamBudgetsQuery.data?.result?.total || 0}
            loading={teamBudgetsQuery.isLoading}
            columns={columns}
            pagination
            rowsPerPageOptions={[5, 10, 15, 25, 50, 100]}
            density="comfortable"
            getRowId={row => row.id}
            disableColumnMenu
            disableSelectionOnClick
            paginationMode="server"
            page={page - 1}
            pageSize={pageSize}
            onPageChange={newPage => setPage(newPage + 1)}
            onPageSizeChange={newPageSize => setPageSize(newPageSize)}
            localeText={{
              noRowsLabel: t("teams.teamProfile.NoRows"),
              MuiTablePagination: {
                labelRowsPerPage: t("teams.teamProfile.RowsPerPage")
              }
            }}
            onRowClick={({ row }) => {
              navigate(`/finances/temp/budget/${row.team.id}`);
            }}
          />
        )}
      </Container>
    </Stack>
  );
};
